import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation, useQuery } from '@apollo/client/react'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// graphql
import { PLAN_ITEMS_TREE_POSITIONS_QUERY, PLANS_DETAILS_QUERY } from 'graphql/plan/queries'
import {
  PLAN_UPDATE_MUTATION,
  PLAN_TREE_UPDATE_MUTATION,
  LINKING_ITEM_DELETE_MUTATION,
  LINKING_ITEM_UPDATE_MUTATION,
  PLAN_ITEM_DELETE_BULK_REFERENCED_MUTATION
} from 'graphql/plan/mutations'
// types
import { IPlanItem } from 'types/graphql'
// utils
import get from 'lodash/get'
import isFunction from 'lodash/isFunction'
import { handleError } from 'utils/errors/handlers'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  deleteReferencedPlanItems: () => Promise<void>
  referencedPlanItems: IPlanItem[]
  unlinkPlanItem: (id: string, sourceEntityDetailsQuery: any, sourceEntityDetailsVariables: any) => void
  updateLinkedItem: (
    id: string,
    input: {
      description: string
      weighting: string
      parentId: string
    },
    sourceEntityDetailsQuery: any,
    sourceEntityDetailsVariables: any
  ) => Promise<void>
  updatePlan: (id: string, parentPlanId: string) => Promise<void>
  updatePlanTree: () => Promise<void>
}

export const LinkedItemsAPIContext = React.createContext<IContext>({} as any)

const LinkedItemsAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const { data: planItemsQuery } = useQuery(PLAN_ITEMS_TREE_POSITIONS_QUERY, {
    skip: !router.query.parentId,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        planId: router.query.id
      }
    }
  })

  const [planUpdateMutation] = useMutation(PLAN_UPDATE_MUTATION)
  const [treeUpdateMutation] = useMutation(PLAN_TREE_UPDATE_MUTATION)
  const [updateLinkingItemMutation] = useMutation(LINKING_ITEM_UPDATE_MUTATION)
  const [deleteLinkingItemMutation] = useMutation(LINKING_ITEM_DELETE_MUTATION)
  const [deleteReferencedPlanItemsMutation] = useMutation(PLAN_ITEM_DELETE_BULK_REFERENCED_MUTATION)

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const planItems = get(planItemsQuery, 'planItems.data', [])
  const referencedPlanItems = planItems.filter((item) => item.referenced).map(({ id }) => id)

  // methods
  const deleteReferencedPlanItems = async () => {
    try {
      await deleteReferencedPlanItemsMutation({
        variables: {
          ids: referencedPlanItems
        }
      })

      await treeQueryRefetch()
    } catch (errors) {
      handleError(errors, 'LinkedItemsAPI.deleteReferencedPlanItems')
    }
  }

  const unlinkPlanItem = async (id, sourceEntityDetailsQuery, sourceEntityDetailsVariables) => {
    try {
      await deleteLinkingItemMutation({
        variables: {
          id: id
        },
        refetchQueries: [
          { query: sourceEntityDetailsQuery, variables: sourceEntityDetailsVariables },
          { query: PLANS_DETAILS_QUERY, variables: { filter: { id: router.query.id } } }
        ]
      })

      if (isFunction(treeQueryRefetch)) {
        await treeQueryRefetch()
      }
    } catch (errors) {
      handleError(errors, 'LinkedItemsActions.unlinkPlanItem')
    }
  }

  const updateLinkedItem = async (id, input, sourceEntityDetailsQuery, sourceEntityDetailsVariables) => {
    await updateLinkingItemMutation({
      variables: {
        id: id,
        input
      },
      refetchQueries: [{ query: sourceEntityDetailsQuery, variables: sourceEntityDetailsVariables }]
    })
  }

  const updatePlan = async (id, parentPlanId) => {
    try {
      await planUpdateMutation({
        variables: {
          id: id,
          input: {
            parentPlanId: parentPlanId
          }
        },
        refetchQueries: [{ query: PLANS_DETAILS_QUERY, variables: { filter: { id: router.query.id } } }]
      })
    } catch (errors) {
      handleError(errors, 'LinkedItemsAPI.updatePlan')
    }
  }

  const updatePlanTree = async () => {
    const filteredPlanItems = planItems
      .filter((item) => !item.referenced)
      .map((item, i) => ({
        id: item.id,
        expanded: item.expanded,
        treePosition: i,
        parentId: referencedPlanItems.some((id) => id === item.parentId) ? null : item.parentId
      }))

    try {
      await treeUpdateMutation({
        variables: {
          input: filteredPlanItems
        }
      })
    } catch (errors) {
      handleError(errors, 'LinkedItemsAPI.updatePlan')
    }
  }

  const value = {
    updatePlan,
    unlinkPlanItem,
    updatePlanTree,
    updateLinkedItem,
    referencedPlanItems,
    deleteReferencedPlanItems
  }

  return <LinkedItemsAPIContext.Provider value={value}>{children}</LinkedItemsAPIContext.Provider>
}

export default LinkedItemsAPI
